import { Box, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

interface ShopifyIntegrationFormProps {
  editMode?: boolean;
  initialData?: {
    shopUrl?: string;
    apiKey?: string;
    apiSecret?: string;
    apiVersion?: string;
  };
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: {
    shopUrl: string;
    apiKey: string;
    apiSecret: string;
    apiVersion: string;
  }) => void;
}

export default function ShopifyIntegrationForm({
  editMode = false,
  initialData,
  onValidationChange,
  onDataChange,
}: ShopifyIntegrationFormProps) {
  const [shopUrl, setShopUrl] = useState(initialData?.shopUrl || "");
  const [apiKey, setApiKey] = useState(initialData?.apiKey || "");
  const [apiSecret, setApiSecret] = useState(initialData?.apiSecret || "");
  const [apiVersion, setApiVersion] = useState(
    initialData?.apiVersion || "2024-01"
  );

  // Validate form
  useEffect(() => {
    const isValid =
      shopUrl.trim() !== "" &&
      apiKey.trim() !== "" &&
      apiSecret.trim() !== "" &&
      apiVersion.trim() !== "";
    onValidationChange(isValid);
  }, [shopUrl, apiKey, apiSecret, apiVersion, onValidationChange]);

  // Notify parent of data changes
  useEffect(() => {
    onDataChange({
      shopUrl,
      apiKey,
      apiSecret,
      apiVersion,
    });
  }, [shopUrl, apiKey, apiSecret, apiVersion, onDataChange]);

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Connect your Shopify store to publish blog posts directly from the content engine.
      </Typography>

      <Typography variant="body2" sx={{ mb: 2, color: "warning.main" }}>
        <strong>Important:</strong> For legacy custom apps, you need the Admin API access token
        (not the API secret key). Find it in your Shopify admin under Apps → Develop apps →
        Your App → API credentials → Admin API access token.
      </Typography>

      <TextField
        sx={{ mt: 1 }}
        fullWidth
        value={shopUrl}
        label="Store URL"
        placeholder="mystore.myshopify.com"
        helperText="Your Shopify store URL (without https://)"
        onChange={(e) => setShopUrl(e.target.value)}
        required
      />

      <TextField
        sx={{ mt: 2 }}
        fullWidth
        value={apiKey}
        label="API Key"
        placeholder="4a1facb53d8c36cbe90faa8440e68ba0"
        helperText="Your Shopify app's API key (found in API credentials)"
        onChange={(e) => setApiKey(e.target.value)}
        required
      />

      <TextField
        sx={{ mt: 2 }}
        fullWidth
        type="password"
        value={apiSecret}
        label="Admin API Access Token"
        placeholder="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"
        helperText="⚠️ Use the Admin API access token, NOT the API secret key. It usually starts with 'shpat_' or 'shpca_'"
        onChange={(e) => setApiSecret(e.target.value)}
        required
      />

      <TextField
        sx={{ mt: 2 }}
        fullWidth
        value={apiVersion}
        label="API Version (Optional)"
        placeholder="2024-01"
        helperText="Leave as default unless you need a specific version"
        onChange={(e) => setApiVersion(e.target.value)}
        required
      />

      <Typography variant="caption" sx={{ mt: 2, display: "block", color: "text.secondary" }}>
        <strong>Required permissions:</strong> Your Shopify app needs write access to blogs and articles.
      </Typography>
    </Box>
  );
}
